import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  EditCellV8,
  CellCenterV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  EyeColumnV8,
  toastFailure,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  isActiveColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
} from 'shared-pages/common/grid-helpfunc';
import {
  collectorsWhere,
  useGetCollectorAsOptionsQuery,
} from 'shared-pages/components';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { useHandleError } from 'shared-pages/services';
import { convertFieldToFloat } from 'shared-pages/common/form-helpfunc';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateVehicle] = useMutation(gqlUpdate);
  const [addNewVehicle] = useMutation(gqlAdd);
  const [deleteVehicle] = useMutation(gqlDelete);

  const { handleGraphqlError } = useHandleError('vehicle');

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Vehicle'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'plateNumber',
        header: t('Αριθμός Κυκλοφορίας Οχήματος'),
        columnName: t('Αριθμός Κυκλοφορίας Οχήματος'),
        accessorKey: 'plateNumber',
        orderByField: 'plateNumber',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      },
      /* {
        id: 'brand',
        header: t('Μάρκα Οχήματος'),
        columnName: t('Μάρκα Οχήματος'),
        accessorKey: 'brand',
        orderByField: 'brand',
        cell: TableCellV8,
        size: 50,
        meta: { type: 'text' },
      }, */
      ...(appStore.isPlatformOwner
        ? [
            {
              id: 'collectorId',
              header: t('Συλλέκτης'),
              columnName: t('Συλλέκτης'),
              accessorKey: 'collectorId',
              orderByField: 'collector.name',
              cell: TableCellV8,
              size: 50,
              meta: {
                type: 'select',
                hook: useGetCollectorAsOptionsQuery,
                where: collectorsWhere,
                idKey: 'collectorId',
                idName: 'collector.name',
              },
            },
          ]
        : []),
      {
        id: 'grossWeight',
        header: t('Μικτό Φορτίο'),
        columnName: t('Μικτό Φορτίο'),
        accessorKey: 'grossWeight',
        orderByField: 'grossWeight',
        cell: TableCellV8,
        size: 50,
        meta: {
          type: 'number',
        },
      },
      {
        id: 'payloadWeight',
        header: t('Οφέλιμο Φορτίο'),
        columnName: t('Οφέλιμο Φορτίο'),
        accessorKey: 'payloadWeight',
        orderByField: 'payloadWeight',
        cell: TableCellV8,
        size: 50,
        meta: {
          type: 'number',
        },
      },

      isActiveColumn(t, i18n),
      ...(!isGridInForm && appStore.isPlatformOwner
        ? [
            {
              id: 'edit',
              header: t('Ενέργειες'),
              columnName: t('Ενέργειες'),
              accessorKey: 'edit',
              orderByField: 'lastUser',
              size: 120,
              cell: ({ row, table }) => {
                return true ? EditCellV8({ row, table }) : <>-</>;
              },
            },
          ]
        : []),
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          style={{ overflow: 'visible' }}
          ref={tableRef}
          loading={loading}
          id="vehicles-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
              collectorId: appStore.isCollector ? appStore.associatedId : undefined,
            };
          }}
          onBeforeSaveRow={async (state, row, changes) => {
            console.log('$$$ onBeforeSaveRow', state, row, changes);
            if (row?.isNotActive !== undefined && row.isNotActive === true) return true;
            // Validate
            if (!row.collectorId && !changes.collectorId) {
              toastFailure(t('Πρέπει να δηλώσετε Συλλέκτη'));
              return false;
            }
            return true;
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Vehicles - OnSaveRow row', row, changes);
            const values =
              tableStore.state === TableDataState.insert ? { ...row } : { id: id, ...changes }; // Το changes δεν έχει το collectorId
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));

            convertFieldToFloat(mutValues, ['payloadWeight', 'grossWeight']);

            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewVehicle({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                  handleGraphqlError('mutation-add-failed', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addVehicle.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateVehicle({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow update error reason', reason);
                  handleGraphqlError('mutation-update-failed', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε το Όχημα <strong>${row.plateNumber}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteVehicle({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
              handleGraphqlError('mutation-delete-failed', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε το Όχημα');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
